import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  GitCompare, ArrowRight, TrendingDown, Clock, DollarSign, Zap, BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { fadeInUp } from "@/config/animations";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_COLORS = {
  crewai: "#10b981", autogen: "#3b82f6", langgraph: "#f59e0b", manus: "#8b5cf6", native: "#06b6d4",
};

const PRESETS = {
  "all-crewai": { name: "All CrewAI", platforms: { research: "crewai", analyze: "crewai", write: "crewai", review: "crewai" } },
  "all-autogen": { name: "All AutoGen", platforms: { research: "autogen", analyze: "autogen", write: "autogen", review: "autogen" } },
  "mixed": { name: "Mixed Platforms", platforms: { research: "crewai", analyze: "autogen", write: "langgraph", review: "manus" } },
  "cost-optimized": { name: "Cost Optimized", platforms: { research: "native", analyze: "native", write: "native", review: "native" } },
};

const BASE_TASKS = [
  { id: "research", name: "Deep Research", agent: "gpt-4o", estimated_tokens: 5000, dependencies: [], capabilities_required: ["reasoning", "research"] },
  { id: "analyze", name: "Data Analysis", agent: "claude-sonnet", estimated_tokens: 4000, dependencies: ["research"], capabilities_required: ["reasoning", "analysis"] },
  { id: "write", name: "Content Writing", agent: "gpt-4o", estimated_tokens: 8000, dependencies: ["analyze"], capabilities_required: ["creative"] },
  { id: "review", name: "Quality Review", agent: "claude-haiku", estimated_tokens: 2000, dependencies: ["write"], capabilities_required: ["reasoning"] },
];

export default function WorkflowComparison() {
  const [configA, setConfigA] = useState("all-crewai");
  const [configB, setConfigB] = useState("mixed");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const runComparison = async () => {
    setLoading(true);
    try {
      const makeWorkflow = (presetKey) => ({
        name: PRESETS[presetKey].name,
        tasks: BASE_TASKS.map(t => ({ ...t, platform: PRESETS[presetKey].platforms[t.id] || "native" })),
      });

      const [resA, resB] = await Promise.all([
        axios.post(`${API}/orchestrate`, { workflow: makeWorkflow(configA), auto_execute: false, super_optimize: true }),
        axios.post(`${API}/orchestrate`, { workflow: makeWorkflow(configB), auto_execute: false, super_optimize: true }),
      ]);

      setResults({ a: resA.data, b: resB.data });
      toast.success("Comparison complete!");
    } catch (e) {
      toast.error("Comparison failed");
    } finally {
      setLoading(false);
    }
  };

  const optA = results?.a?.optimization;
  const optB = results?.b?.optimization;

  const comparisonData = optA && optB ? [
    { metric: "Cost Reduction", a: optA.cost_reduction_percent, b: optB.cost_reduction_percent },
    { metric: "Time Reduction", a: optA.time_reduction_percent, b: optB.time_reduction_percent },
    { metric: "Original Cost", a: optA.original_cost * 10000, b: optB.original_cost * 10000 },
    { metric: "Optimized Cost", a: optA.optimized_cost * 10000, b: optB.optimized_cost * 10000 },
  ] : [];

  return (
    <motion.div {...fadeInUp} className="space-y-5" data-testid="workflow-comparison">
      <div>
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <GitCompare className="w-5 h-5 text-[var(--qn-primary)]" /> Workflow Comparison
        </h3>
        <p className="text-sm text-[var(--qn-foreground-muted)]">Compare the same workflow across different platform configurations</p>
      </div>

      {/* Config Selectors */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex-1 min-w-[180px]">
          <label className="text-xs font-medium text-[var(--qn-foreground-muted)] block mb-1">Configuration A</label>
          <Select value={configA} onValueChange={setConfigA}>
            <SelectTrigger className="bg-[var(--qn-background)] border-[var(--qn-border)]" data-testid="compare-config-a"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[var(--qn-card)] border-[var(--qn-border)]">
              {Object.entries(PRESETS).map(([k, v]) => <SelectItem key={k} value={k}>{v.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <ArrowRight className="w-5 h-5 text-[var(--qn-foreground-subtle)] mt-5 shrink-0" />

        <div className="flex-1 min-w-[180px]">
          <label className="text-xs font-medium text-[var(--qn-foreground-muted)] block mb-1">Configuration B</label>
          <Select value={configB} onValueChange={setConfigB}>
            <SelectTrigger className="bg-[var(--qn-background)] border-[var(--qn-border)]" data-testid="compare-config-b"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[var(--qn-card)] border-[var(--qn-border)]">
              {Object.entries(PRESETS).map(([k, v]) => <SelectItem key={k} value={k}>{v.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <Button onClick={runComparison} disabled={loading || configA === configB} className="btn-primary mt-5" data-testid="run-comparison-btn">
          {loading ? (
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity }} className="w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
          ) : (
            <><GitCompare className="w-4 h-4 mr-2" /> Compare</>
          )}
        </Button>
      </div>

      {/* Results */}
      {results && optA && optB && (
        <>
          {/* Side-by-side Metrics */}
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: "Config A", name: PRESETS[configA]?.name, opt: optA, color: "#06b6d4" },
              { label: "Config B", name: PRESETS[configB]?.name, opt: optB, color: "#8b5cf6" },
            ].map((cfg, ci) => (
              <Card key={ci} className="card-themed" style={{ borderColor: cfg.color + "40" }}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base" style={{ color: cfg.color }}>{cfg.name}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-[var(--qn-background)] rounded-lg p-2.5">
                      <div className="text-xs text-[var(--qn-foreground-muted)] flex items-center gap-1"><TrendingDown className="w-3 h-3" /> Cost Reduction</div>
                      <div className="text-lg font-bold" style={{ color: cfg.color }}>{cfg.opt.cost_reduction_percent}%</div>
                    </div>
                    <div className="bg-[var(--qn-background)] rounded-lg p-2.5">
                      <div className="text-xs text-[var(--qn-foreground-muted)] flex items-center gap-1"><Clock className="w-3 h-3" /> Time Reduction</div>
                      <div className="text-lg font-bold" style={{ color: cfg.color }}>{cfg.opt.time_reduction_percent}%</div>
                    </div>
                    <div className="bg-[var(--qn-background)] rounded-lg p-2.5">
                      <div className="text-xs text-[var(--qn-foreground-muted)] flex items-center gap-1"><DollarSign className="w-3 h-3" /> Original</div>
                      <div className="text-sm font-mono">${cfg.opt.original_cost?.toFixed(4)}</div>
                    </div>
                    <div className="bg-[var(--qn-background)] rounded-lg p-2.5">
                      <div className="text-xs text-[var(--qn-foreground-muted)] flex items-center gap-1"><Zap className="w-3 h-3" /> Optimized</div>
                      <div className="text-sm font-mono">${cfg.opt.optimized_cost?.toFixed(4)}</div>
                    </div>
                  </div>
                  {/* Platform badges */}
                  <div className="flex gap-1.5 flex-wrap">
                    {(cfg.opt.platforms_used || []).map(p => (
                      <Badge key={p} style={{ backgroundColor: PLATFORM_COLORS[p] + "20", color: PLATFORM_COLORS[p] }} className="text-xs capitalize">{p}</Badge>
                    ))}
                  </div>
                  {/* Data transfers */}
                  <div className="text-xs text-[var(--qn-foreground-muted)]">
                    Data Transfers: {cfg.opt.data_transfers?.length || 0} | Latency: {cfg.opt.total_transfer_latency_ms || 0}ms
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Winner Callout */}
          <Card className="card-themed border-[var(--qn-primary)]/30">
            <CardContent className="p-4 flex items-center gap-4">
              <BarChart3 className="w-8 h-8 text-[var(--qn-primary)] shrink-0" />
              <div>
                <div className="text-base font-semibold">
                  {optA.cost_reduction_percent > optB.cost_reduction_percent
                    ? `${PRESETS[configA]?.name} saves ${(optA.cost_reduction_percent - optB.cost_reduction_percent).toFixed(1)}% more`
                    : optB.cost_reduction_percent > optA.cost_reduction_percent
                    ? `${PRESETS[configB]?.name} saves ${(optB.cost_reduction_percent - optA.cost_reduction_percent).toFixed(1)}% more`
                    : "Both configurations perform equally"}
                </div>
                <p className="text-sm text-[var(--qn-foreground-muted)]">
                  Cross-platform optimization can unlock significant savings compared to single-platform workflows
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Comparison Chart */}
          <Card className="card-themed">
            <CardHeader className="pb-2"><CardTitle className="text-base">Side-by-Side Comparison</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={comparisonData} barGap={4}>
                  <XAxis dataKey="metric" tick={{ fontSize: 11, fill: "var(--qn-foreground-muted)" }} />
                  <YAxis tick={{ fontSize: 10, fill: "var(--qn-foreground-muted)" }} />
                  <Tooltip contentStyle={{ background: "var(--qn-card)", border: "1px solid var(--qn-border)", borderRadius: 8, fontSize: 12, color: "var(--qn-foreground)" }} wrapperStyle={{ outline: "none" }} cursor={{ fill: "rgba(255,255,255,0.06)" }} />
                  <Bar dataKey="a" name={PRESETS[configA]?.name} fill="#06b6d4" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="b" name={PRESETS[configB]?.name} fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </>
      )}
    </motion.div>
  );
}
